import React, { useEffect, useMemo, useState } from 'react';
import {
  clearWorkspaceAllowance,
  getCreditPolicy,
  listWorkspaceAllowances,
  listWorkspaces,
  setDefaultCreditsPerAsset,
  setEditionCreditsPerAsset,
  setWorkspaceAllowance,
  type CreditPolicyRow,
  type WorkspaceAllowanceRow,
  type WorkspaceRow,
} from './api';

/** Monthly included AI credits PER ACTIVE ASSET, editable at all three levels:
 *  global default → edition override → per-workspace negotiated allowance.
 *  The server resolves them in that order (highest wins) on every grant. */
export default function CreditPolicy({ toast }: { toast: (m: string) => void }) {
  const [open, setOpen] = useState(false);
  const [policy, setPolicy] = useState<CreditPolicyRow[]>([]);
  const [allowances, setAllowances] = useState<WorkspaceAllowanceRow[]>([]);
  const [workspaces, setWorkspaces] = useState<WorkspaceRow[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [wsId, setWsId] = useState('');
  const [wsCredits, setWsCredits] = useState('');
  const [wsReason, setWsReason] = useState('');
  const [wsExpires, setWsExpires] = useState('');

  const load = async () => {
    const [rows, grants, all] = await Promise.all([
      getCreditPolicy(),
      listWorkspaceAllowances(),
      listWorkspaces(),
    ]);
    setPolicy(rows);
    setAllowances(grants);
    setWorkspaces(all);
    setDraft(
      Object.fromEntries(rows.map((r) => [r.scope_key, r.credits_per_asset == null ? '' : String(r.credits_per_asset)]))
    );
  };
  useEffect(() => {
    void load();
  }, []);

  const globalRow = policy.find((r) => r.scope === 'global');
  const editions = useMemo(() => policy.filter((r) => r.scope === 'edition'), [policy]);

  const saveRow = async (row: CreditPolicyRow) => {
    const raw = (draft[row.scope_key] ?? '').trim();
    if (row.scope === 'global' && raw === '') {
      toast('The global default cannot be blank');
      return;
    }
    const credits = raw === '' ? null : Number(raw);
    if (credits != null && (!Number.isInteger(credits) || credits < 0)) {
      toast('Enter a whole number of credits, e.g. 25');
      return;
    }
    try {
      if (row.scope === 'global') await setDefaultCreditsPerAsset(credits as number);
      else await setEditionCreditsPerAsset(row.scope_key, credits);
      toast(`${row.display_name}: ${credits == null ? 'inherits default' : `${credits} credits / asset / month`}`);
      await load();
    } catch (e) {
      toast(`Could not save: ${(e as Error).message}`);
    }
  };

  const saveAllowance = async () => {
    const credits = Number((wsCredits ?? '').trim());
    if (!wsId) {
      toast('Pick a workspace');
      return;
    }
    if (!Number.isInteger(credits) || credits < 0) {
      toast('Enter a whole number of credits');
      return;
    }
    try {
      await setWorkspaceAllowance(
        wsId,
        credits,
        wsReason.trim() || null,
        wsExpires ? new Date(wsExpires).toISOString() : null
      );
      toast('Workspace allowance saved');
      setWsCredits('');
      setWsReason('');
      setWsExpires('');
      await load();
    } catch (e) {
      toast(`Could not save: ${(e as Error).message}`);
    }
  };

  const clearAllowance = async (accountId: string) => {
    try {
      await clearWorkspaceAllowance(accountId);
      toast('Allowance removed — workspace falls back to its edition');
      await load();
    } catch (e) {
      toast(`Could not remove: ${(e as Error).message}`);
    }
  };

  return (
    <div className="howto" style={{ marginBottom: 16 }}>
      <button className="howto-head" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className="caret">{open ? '▾' : '▸'}</span>
        AI credits per asset
        <span className="muted" style={{ fontWeight: 400, marginLeft: 8 }}>
          {globalRow ? `Default ${globalRow.effective_credits_per_asset}/mo` : ''}
          {editions.length
            ? '   ·   ' + editions.map((e) => `${e.display_name} ${e.effective_credits_per_asset}`).join('   ·   ')
            : ''}
          {allowances.length ? `   ·   ${allowances.length} workspace override(s)` : ''}
        </span>
      </button>
      {open ? (
        <div style={{ padding: 16 }}>
          <p className="hint" style={{ marginTop: 0 }}>
            Credits granted each month per <strong>active billable asset</strong>. A workspace allowance beats its
            edition, which beats the global default. Raising a rate tops the current period up on the next
            reconcile; lowering it applies from the next period (credits already granted are never clawed back).
          </p>

          <div
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}
          >
            {globalRow ? (
              <div key="global" className="card" style={{ boxShadow: 'none' }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>Global default</div>
                <label style={{ marginTop: 0 }}>Credits / asset / month</label>
                <input
                  value={draft.assets ?? ''}
                  placeholder="10"
                  onChange={(e) => setDraft((d) => ({ ...d, assets: e.target.value }))}
                  onBlur={() => void saveRow(globalRow)}
                  onKeyDown={(e) => e.key === 'Enter' && void saveRow(globalRow)}
                  style={{ maxWidth: 120 }}
                />
                <p className="hint" style={{ marginTop: 8 }}>Applies to every workspace with no override.</p>
              </div>
            ) : null}

            {editions.map((ed) => (
              <div key={ed.scope_key} className="card" style={{ boxShadow: 'none' }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>{ed.display_name}</div>
                <label style={{ marginTop: 0 }}>Credits / asset / month</label>
                <input
                  value={draft[ed.scope_key] ?? ''}
                  placeholder={`inherit (${globalRow?.effective_credits_per_asset ?? 10})`}
                  onChange={(e) => setDraft((d) => ({ ...d, [ed.scope_key]: e.target.value }))}
                  onBlur={() => void saveRow(ed)}
                  onKeyDown={(e) => e.key === 'Enter' && void saveRow(ed)}
                  style={{ maxWidth: 120 }}
                />
                <p className="hint" style={{ marginTop: 8 }}>
                  {ed.credits_per_asset == null
                    ? `Inheriting ${ed.effective_credits_per_asset}. Blank = inherit.`
                    : `Effective ${ed.effective_credits_per_asset}. Blank to inherit.`}
                </p>
              </div>
            ))}
          </div>

          <h3 style={{ marginTop: 22, marginBottom: 6 }}>Workspace overrides</h3>
          <p className="hint" style={{ marginTop: 0 }}>
            Negotiated allowance for one customer. Optional end date; leave blank to keep it open-ended.
          </p>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <select value={wsId} onChange={(e) => setWsId(e.target.value)} style={{ maxWidth: 260 }}>
              <option value="">Choose a workspace…</option>
              {workspaces.map((w) => (
                <option key={w.account_id} value={w.account_id}>
                  {w.name ?? w.account_id} {w.edition_key ? `(${w.edition_key})` : ''}
                </option>
              ))}
            </select>
            <input
              value={wsCredits}
              placeholder="credits / asset"
              onChange={(e) => setWsCredits(e.target.value)}
              style={{ maxWidth: 130 }}
            />
            <input
              value={wsReason}
              placeholder="reason (e.g. 2026 enterprise deal)"
              onChange={(e) => setWsReason(e.target.value)}
              style={{ maxWidth: 260 }}
            />
            <input type="date" value={wsExpires} onChange={(e) => setWsExpires(e.target.value)} style={{ maxWidth: 170 }} />
            <button className="btn" onClick={() => void saveAllowance()}>
              Save allowance
            </button>
          </div>

          {allowances.length ? (
            <table style={{ marginTop: 14, width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Workspace</th>
                  <th style={{ textAlign: 'left' }}>Edition</th>
                  <th style={{ textAlign: 'left' }}>Credits / asset</th>
                  <th style={{ textAlign: 'left' }}>Reason</th>
                  <th style={{ textAlign: 'left' }}>Ends</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {allowances.map((a) => (
                  <tr key={a.account_id + a.effective_from}>
                    <td>{a.account_name ?? a.account_id}</td>
                    <td className="muted">{a.edition_key ?? '—'}</td>
                    <td>{a.credits_per_asset_monthly}</td>
                    <td className="muted">{a.reason ?? '—'}</td>
                    <td className="muted">{a.expires_at ? new Date(a.expires_at).toLocaleDateString() : 'open-ended'}</td>
                    <td>
                      <button onClick={() => void clearAllowance(a.account_id)}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="hint">No workspace overrides — everyone is on their edition rate.</p>
          )}
        </div>
      ) : null}
    </div>
  );
}
